import * as THREE from 'three';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { ShaderPass } from 'three/addons/postprocessing/ShaderPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';

const SETTINGS = Object.freeze({
  distortion: 0.05,
  aberration: 0.17,
  aberrationRest: 0.105,
  // Figma measures Y from the top. GLSL UVs measure it from the bottom.
  center: new THREE.Vector2(0.464, 1 - 0.4715),
  centerDriftX: 0.0045,
  cycleSeconds: 2.45,
  pulseRest: 0.78,
  pulsePeak: 1.76,
  trailRest: 0.64,
  trailPeak: 0.97,
  maxPixelRatio: 2,
  designWidth: 1280,
  designHeight: 900,
});

const LensDistortionShader = {
  name: 'LateralLensDistortion',
  uniforms: {
    tDiffuse: { value: null },
    uDistortion: { value: SETTINGS.distortion },
    uAberration: { value: SETTINGS.aberration },
    uCenter: { value: SETTINGS.center.clone() },
    uResolution: { value: new THREE.Vector2(1, 1) },
    uPulse: { value: 1 },
    uTrailStrength: { value: SETTINGS.trailPeak },
    uResponsiveStrength: { value: 1 },
    uLeftBalance: { value: 1.45 },
  },
  vertexShader: /* glsl */ `
    varying vec2 vUv;

    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: /* glsl */ `
    precision highp float;

    uniform sampler2D tDiffuse;
    uniform float uDistortion;
    uniform float uAberration;
    uniform vec2 uCenter;
    uniform vec2 uResolution;
    uniform float uPulse;
    uniform float uTrailStrength;
    uniform float uResponsiveStrength;
    uniform float uLeftBalance;

    varying vec2 vUv;

    float insideViewport(vec2 uv) {
      vec2 lower = step(vec2(0.0), uv);
      vec2 upper = step(uv, vec2(1.0));
      return lower.x * lower.y * upper.x * upper.y;
    }

    vec4 sampleSafe(vec2 uv) {
      return texture2D(tDiffuse, uv) * insideViewport(uv);
    }

    vec2 distortedUv(
      vec2 fromCenter,
      float radiusSquared,
      float strength
    ) {
      // This calibration converts Figma's normalized distortion amount into
      // UV displacement. Positive strength expands content away from center.
      // This inverse-square-root curve is strictly monotonic: the sampling
      // coordinate can compress toward the lens center, but it can never turn
      // back and reveal a mirrored copy of the source. The larger coefficient
      // retains the aggressive outer-letter pull of the reference.
      float radialScale = inversesqrt(
        1.0 + strength * radiusSquared * 46.0
      );
      vec2 warped = fromCenter;
      // Lateral mode: displace horizontally while keeping baselines stable.
      warped.x *= radialScale;
      // A small vertical response gives the outer glyphs the lens-flare wedge
      // visible in the reference without turning the effect fully radial.
      warped.y *= inversesqrt(
        1.0 + strength * radiusSquared * 6.4
      );
      return uCenter + warped;
    }

    void main() {
      float aspect = uResolution.x / uResolution.y;

      // Measure the lens relative to the canvas width. The same text position
      // now receives the same distortion when only the viewport height changes.
      vec2 fromCenter = vUv - uCenter;
      vec2 lensSpace = fromCenter;
      lensSpace.y /= aspect;

      // Horizontal distance drives the effect; vertical distance only adds a
      // small lens falloff instead of bending the glyphs vertically.
      float radiusSquared = (
        lensSpace.x * lensSpace.x
        + lensSpace.y * lensSpace.y * 0.18
      );
      float radius = sqrt(radiusSquared);

      // Keep a restrained remnant on FLAWLESS, but let its outer F and S bend
      // visibly. A wider vertical transition avoids a horizontal seam between
      // the two lines.
      float lowerLineInfluence = smoothstep(
        0.0,
        0.10,
        -lensSpace.y
      );
      float regionStrength = mix(0.44, 1.0, lowerLineInfluence);
      // Balance the visible trails around an off-center focal point. The lens
      // origin remains 46.4%; only the response curve is normalized per side.
      float sideBalance = mix(
        uLeftBalance,
        1.0,
        step(0.0, fromCenter.x)
      );
      float strength = (
        uDistortion
        * uPulse
        * regionStrength
        * sideBalance
        * uResponsiveStrength
      );

      // Lateral chromatic aberration is modeled as a different lens curve per
      // channel. This keeps the center registered and increases separation at
      // the outer letters, like real lateral chromatic aberration.
      float redStrength = strength * (1.0 + uAberration);
      float greenStrength = strength;
      float blueStrength = strength * (1.0 - uAberration);

      vec2 redUv = distortedUv(fromCenter, radiusSquared, redStrength);
      vec2 greenUv = distortedUv(fromCenter, radiusSquared, greenStrength);
      vec2 blueUv = distortedUv(fromCenter, radiusSquared, blueStrength);

      vec4 redSharp = sampleSafe(redUv);
      vec4 greenSharp = sampleSafe(greenUv);
      vec4 blueSharp = sampleSafe(blueUv);
      vec3 sharpColor = vec3(redSharp.r, greenSharp.g, blueSharp.b);
      // An asymptotic mask has no inner or outer cutoff ring.
      float chromaMask = 1.0 - exp(-radiusSquared * 10.0);
      float chromaInfluence = chromaMask * regionStrength;
      sharpColor.b *= mix(1.0, 2.8, chromaInfluence);
      sharpColor.b = max(
        sharpColor.b,
        blueSharp.g * chromaInfluence * 0.62
      );
      float sharpAlpha = max(redSharp.a, max(greenSharp.a, blueSharp.a));

      // The reference is not a single distortion sample. It accumulates several
      // lens strengths, producing a radial optical trail on the outer glyphs.
      vec3 trailColor = vec3(0.0);
      float trailAlpha = 0.0;

      for (int index = 0; index < 25; index++) {
        float progress = float(index) / 24.0;
        // Begin slightly inside the lens instead of at zero distortion. The
        // outer L and S now inherit the arc rather than leaving straight,
        // undistorted letter remnants behind. The final sample still fades to
        // zero weight, preserving the soft outer end.
        float sampleScale = mix(
          0.14,
          2.35,
          pow(progress, 1.15)
        );
        // Ease both ends of the trail to zero. The old front-loaded weight
        // preserved a readable copy of the first/last glyph behind the curve.
        float trailEnvelope = max(
          sin(progress * 3.14159265),
          0.0
        );
        float weight = pow(trailEnvelope, 0.72);

        vec2 trailRedUv = distortedUv(
          fromCenter,
          radiusSquared,
          redStrength * sampleScale
        );
        vec2 trailGreenUv = distortedUv(
          fromCenter,
          radiusSquared,
          greenStrength * sampleScale
        );
        vec2 trailBlueUv = distortedUv(
          fromCenter,
          radiusSquared,
          blueStrength * sampleScale
        );

        vec4 trailRed = sampleSafe(trailRedUv);
        vec4 trailGreen = sampleSafe(trailGreenUv);
        vec4 trailBlue = sampleSafe(trailBlueUv);

        vec3 trailRgb = vec3(
          trailRed.r,
          trailGreen.g,
          trailBlue.b
        );
        trailRgb.b *= mix(1.0, 3.15, chromaInfluence);
        trailRgb.b = max(
          trailRgb.b,
          trailBlue.g * chromaInfluence * 0.72
        );
        float sampledAlpha = max(
          trailRed.a,
          max(trailGreen.a, trailBlue.a)
        );

        // Add the sub-pixel samples with a low per-sample opacity. Their overlap
        // forms one continuous curved smear instead of several readable copies.
        trailColor = min(
          trailColor + trailRgb * weight * 0.075,
          vec3(1.35)
        );
        trailAlpha = min(
          trailAlpha + sampledAlpha * weight * 0.075,
          1.0
        );
      }

      // This continuous falloff starts gently at the center and approaches full
      // strength without ever hitting a finite inner or outer boundary.
      float edgeMask = 1.0 - exp(-radiusSquared * 13.5);
      float trailMix = edgeMask * uTrailStrength;

      vec3 color = mix(sharpColor, trailColor, trailMix);
      color += trailColor * trailMix * 0.24;
      float alpha = mix(sharpAlpha, max(sharpAlpha, trailAlpha), trailMix);

      // Feather the viewport boundary so the effect does not terminate on a
      // visible vertical line.
      float edgePixels = 18.0 / uResolution.x;
      float horizontalBounds = smoothstep(0.0, edgePixels, vUv.x)
        * (1.0 - smoothstep(1.0 - edgePixels, 1.0, vUv.x));

      gl_FragColor = vec4(color, alpha) * horizontalBounds;
    }
  `,
};

function createLensEffect(stage) {
const outputCanvas = stage.querySelector('[data-lens-canvas]');
const titleElements = Array.from(stage.querySelectorAll('[data-lens-line]'));

if (!outputCanvas || titleElements.length === 0) {
  return;
}

const sourceCanvas = document.createElement('canvas');
const sourceContext = sourceCanvas.getContext('2d', { alpha: true });

let renderer;
let shaderFailed = false;

try {
  renderer = new THREE.WebGLRenderer({
    canvas: outputCanvas,
    alpha: true,
    antialias: true,
    premultipliedAlpha: false,
    powerPreference: 'high-performance',
  });
} catch (error) {
  console.error('WebGL could not be initialized. The HTML fallback remains visible.', error);
  throw error;
}

renderer.setClearColor(0x000000, 0);
renderer.outputColorSpace = THREE.SRGBColorSpace;
renderer.debug.onShaderError = (
  gl,
  program,
  vertexShader,
  fragmentShader,
) => {
  shaderFailed = true;
  console.error(
    'The lens shader could not compile.',
    gl.getProgramInfoLog(program),
    gl.getShaderInfoLog(vertexShader),
    gl.getShaderInfoLog(fragmentShader),
  );
};

const scene = new THREE.Scene();
const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

const sourceTexture = new THREE.CanvasTexture(sourceCanvas);
sourceTexture.colorSpace = THREE.SRGBColorSpace;
sourceTexture.minFilter = THREE.LinearFilter;
sourceTexture.magFilter = THREE.LinearFilter;
sourceTexture.wrapS = THREE.ClampToEdgeWrapping;
sourceTexture.wrapT = THREE.ClampToEdgeWrapping;
sourceTexture.generateMipmaps = false;

const sourceMaterial = new THREE.MeshBasicMaterial({
  map: sourceTexture,
  transparent: true,
  depthTest: false,
  depthWrite: false,
});

const sourcePlane = new THREE.Mesh(
  new THREE.PlaneGeometry(2, 2),
  sourceMaterial,
);
scene.add(sourcePlane);

const renderTarget = new THREE.WebGLRenderTarget(1, 1, {
  minFilter: THREE.LinearFilter,
  magFilter: THREE.LinearFilter,
  format: THREE.RGBAFormat,
  type: THREE.UnsignedByteType,
  depthBuffer: false,
  stencilBuffer: false,
});
renderTarget.texture.colorSpace = THREE.SRGBColorSpace;

const composer = new EffectComposer(renderer, renderTarget);
composer.addPass(new RenderPass(scene, camera));

const lensPass = new ShaderPass(LensDistortionShader);
composer.addPass(lensPass);
composer.addPass(new OutputPass());

let animationStartTime = 0;
let resizeTimer = 0;
let renderWidth = 0;
let renderHeight = 0;
let renderPixelRatio = 0;
let responsiveCenterX = SETTINGS.center.x;
let responsiveCenterY = SETTINGS.center.y;

function drawSourceTexture(width, height, pixelRatio) {
  const textureWidth = Math.round(width * pixelRatio);
  const textureHeight = Math.round(height * pixelRatio);
  const textureSizeChanged = (
    sourceCanvas.width !== textureWidth
    || sourceCanvas.height !== textureHeight
  );

  sourceCanvas.width = textureWidth;
  sourceCanvas.height = textureHeight;

  sourceContext.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
  sourceContext.clearRect(0, 0, width, height);

  const stageBounds = stage.getBoundingClientRect();
  const scaleX = stageBounds.width > 0 ? stageBounds.width / width : 1;
  const scaleY = stageBounds.height > 0 ? stageBounds.height / height : 1;

  titleElements.forEach((titleElement) => {
    const elementBounds = titleElement.getBoundingClientRect();
const styles = window.getComputedStyle(titleElement);
const fontSize = Number.parseFloat(styles.fontSize);

/*
 * If the title line contains responsive versions,
 * use only the one currently visible.
 */
const responsiveChildren = Array.from(
  titleElement.children
);

const visibleTextElement =
  responsiveChildren.find(function (element) {
    return window.getComputedStyle(element).display !== 'none';
  }) || titleElement;

let text =
  visibleTextElement.textContent.trim();

    if (styles.textTransform === 'uppercase') {
      text = text.toUpperCase();
    } else if (styles.textTransform === 'lowercase') {
      text = text.toLowerCase();
    } else if (styles.textTransform === 'capitalize') {
      text = text.replace(
        /\b\w/g,
        (character) => character.toUpperCase(),
      );
    }

    sourceContext.textAlign = 'center';
    sourceContext.textBaseline = 'middle';
    sourceContext.fillStyle = styles.color;
    sourceContext.font = [
      styles.fontStyle,
      styles.fontWeight,
      `${fontSize}px`,
      styles.fontFamily,
    ].join(' ');

    if ('letterSpacing' in sourceContext) {
      sourceContext.letterSpacing = styles.letterSpacing === 'normal'
        ? '0px'
        : styles.letterSpacing;
    }

    const textX = (
      elementBounds.left
      - stageBounds.left
      + elementBounds.width * 0.5
    ) / scaleX;
    const textY = (
      elementBounds.top
      - stageBounds.top
      + elementBounds.height * 0.5
    ) / scaleY;

    sourceContext.fillText(text, textX, textY);
  });

  // Changing canvas dimensions can leave the previous GPU allocation alive on
  // some browsers. Dispose it so Three.js uploads a clean texture at the new
  // dimensions instead of sampling fragments of the old layout.
  if (textureSizeChanged) {
    sourceTexture.dispose();
  }
  sourceTexture.needsUpdate = true;
}

function resize(force = false) {
  // The component owns the render size. This works identically in the demo,
  // inside a responsive layout, and when the component becomes a Shopify
  // section; no shader dimensions are taken directly from the browser window.
  const stageBounds = stage.getBoundingClientRect();
  const width = Math.round(stageBounds.width);
  const height = Math.round(stageBounds.height);
  const pixelRatio = Math.min(window.devicePixelRatio || 1, SETTINGS.maxPixelRatio);

  // A display:none responsive layout has no usable render size. Keep its HTML
  // fallback ready and rebuild only after that layout becomes visible again.
  if (width < 2 || height < 2) {
    stage.classList.remove('is-lens-ready');
    return false;
  }

  if (
    !force
    && width === renderWidth
    && height === renderHeight
    && pixelRatio === renderPixelRatio
  ) {
    return false;
  }

  renderWidth = width;
  renderHeight = height;
  renderPixelRatio = pixelRatio;

  renderer.setPixelRatio(pixelRatio);
  renderer.setSize(width, height, false);
  composer.setPixelRatio(pixelRatio);
  composer.setSize(width, height);
  // Recreate both post-processing targets as one atomic resize. This avoids
  // retaining a mobile-sized target when the section expands again.
  composer.reset();

  // Query WebGL's real buffer size rather than estimating width * DPR. This
  // prevents a stale low-resolution mobile buffer from being stretched after
  // the section grows back to desktop dimensions.
  renderer.getDrawingBufferSize(lensPass.uniforms.uResolution.value);

  // Scale the desktop lens offset continuously into any container aspect ratio.
  // There is deliberately no mobile/desktop branch, so dragging through a
  // breakpoint cannot make the distortion jump to a different coordinate map.
  const contentScale = Math.min(
    width / SETTINGS.designWidth,
    height / SETTINGS.designHeight,
  );
  const designCenterOffset = (
    (SETTINGS.center.y - 0.5)
    * SETTINGS.designHeight
  );
  responsiveCenterY = 0.5 + (
    designCenterOffset * contentScale / height
  );

  // The Figma lens is intentionally left-biased on desktop. Blend that bias
  // away on narrow containers so the distortion cannot pull the composition
  // out of a phone viewport.
  const narrowProgress = THREE.MathUtils.clamp(
    (width - 480) / (1100 - 480),
    0,
    1,
  );
  const desktopBlend = smoothstep01(narrowProgress);
  responsiveCenterX = THREE.MathUtils.lerp(
    0.5,
    SETTINGS.center.x,
    desktopBlend,
  );
  lensPass.uniforms.uResponsiveStrength.value = THREE.MathUtils.lerp(
    0.62,
    1.0,
    desktopBlend,
  );
  lensPass.uniforms.uLeftBalance.value = THREE.MathUtils.lerp(
    1.0,
    1.45,
    desktopBlend,
  );

  lensPass.uniforms.uCenter.value.set(
    responsiveCenterX,
    responsiveCenterY,
  );

  drawSourceTexture(width, height, pixelRatio);
  return true;
}

function commitResize(force = false) {
  if (!resize(force)) {
    return false;
  }

  composer.render();

  if (shaderFailed) {
    outputCanvas.hidden = true;
    stage.classList.remove('is-lens-ready');
    return false;
  }

  outputCanvas.hidden = false;
  stage.classList.add('is-lens-ready');
  return true;
}

function scheduleResize() {
  if (resizeTimer) {
    window.clearTimeout(resizeTimer);
  }

  // Keep rendering the existing frame while CSS follows the drag. Once the
  // container settles, rebuild all GPU resources exactly once at its final size.
  resizeTimer = window.setTimeout(() => {
    resizeTimer = 0;
    window.requestAnimationFrame(() => {
      commitResize();
    });
  }, 140);
}

function smoothstep01(value) {
  return value * value * (3 - 2 * value);
}

function render(timeMilliseconds) {
  const time = (timeMilliseconds - animationStartTime) * 0.001;
  const phase = (time / SETTINGS.cycleSeconds) % 1;

  // A broad primary pulse gives the glyphs time to visibly stretch. A smaller
  // second harmonic prevents the motion from feeling like simple CSS scaling.
  const primaryWave = 0.5 + 0.5 * Math.cos(phase * Math.PI * 2);
  const secondaryWave = 0.5 + 0.5 * Math.cos(
    phase * Math.PI * 4 + Math.PI * 0.35,
  );
  const envelope = THREE.MathUtils.clamp(
    smoothstep01(primaryWave) * 0.9 + secondaryWave * 0.1,
    0,
    1,
  );

  const pulse = THREE.MathUtils.lerp(
    SETTINGS.pulseRest,
    SETTINGS.pulsePeak,
    envelope,
  );
  lensPass.uniforms.uPulse.value = pulse;
  lensPass.uniforms.uAberration.value = THREE.MathUtils.lerp(
    SETTINGS.aberrationRest,
    SETTINGS.aberration,
    envelope,
  );
  lensPass.uniforms.uTrailStrength.value = THREE.MathUtils.lerp(
    SETTINGS.trailRest,
    SETTINGS.trailPeak,
    envelope,
  );
  lensPass.uniforms.uCenter.value.set(
    responsiveCenterX
      + Math.sin(phase * Math.PI * 2)
      * SETTINGS.centerDriftX
      * lensPass.uniforms.uResponsiveStrength.value,
    responsiveCenterY,
  );

  // Useful when checking that the live shader is advancing in DevTools.
  outputCanvas.dataset.pulse = pulse.toFixed(3);
  if (renderWidth > 1 && renderHeight > 1 && stage.getClientRects().length > 0) {
    composer.render();
  }
  window.requestAnimationFrame(render);
}

async function start() {
  await document.fonts.ready;
  commitResize(true);

  animationStartTime = performance.now();
  window.requestAnimationFrame(render);
}

window.addEventListener('resize', scheduleResize, { passive: true });
window.addEventListener('orientationchange', scheduleResize, {
  passive: true,
});
window.visualViewport?.addEventListener('resize', scheduleResize, {
  passive: true,
});

const stageResizeObserver = new ResizeObserver(scheduleResize);
stageResizeObserver.observe(stage);

start();
}

document.querySelectorAll('[data-lens-stage]').forEach(createLensEffect);



/* ==========================================================
   DESKTOP ENTRANCE
   LOOKS + ESSENTIALS
   ========================================================== */

function initErickaDesktopEntrances() {

  if (
    !window.matchMedia('(min-width: 768px)').matches
  ) {
    return;
  }


  const groups = [
    document.querySelectorAll(
      '.ericka-home-looks__card'
    ),

    document.querySelectorAll(
      '.ericka-home-essentials__card'
    )
  ];


  groups.forEach(function (cards) {

    if (!cards.length) return;


    const firstCard =
      cards[0];


    const observer =
      new IntersectionObserver(
        function (entries) {

          entries.forEach(function (entry) {

            if (!entry.isIntersecting) return;


            cards.forEach(
              function (card, index) {

                window.setTimeout(
                  function () {

                    card.classList.add(
                      'is-entered'
                    );

                  },

                  index * 90
                );

              }
            );


            observer.disconnect();

          });

        },

        {
          threshold: 0.18
        }
      );


    observer.observe(
      firstCard
    );

  });

}


initErickaDesktopEntrances();